import SessionContext from '@providers/Session/SessionContext';
import rolesConstants from '@utils/rolesConstants';
import { Skeleton } from 'antd';
import React, { ComponentType, lazy, Suspense, useContext } from 'react';

const CHUNK_RELOAD_KEY = 'portal-mot:lorawan-chunk-reload';

type ChunkLoadError = {
  code?: string;
  message?: string;
  name?: string;
};

const isChunkLoadError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;

  const chunkError = error as ChunkLoadError;

  return (
    chunkError.name === 'ChunkLoadError' ||
    chunkError.code === 'CSS_CHUNK_LOAD_FAILED' ||
    /Loading (CSS )?chunk \d+ failed/i.test(chunkError.message ?? '')
  );
};

function lazyLoad<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
) {
  const LazyComponent = lazy(() =>
    importFn().catch(error => {
      if (isChunkLoadError(error)) {
        const currentHref = window.location.href;
        const lastReloadHref = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);

        if (lastReloadHref !== currentHref) {
          window.sessionStorage.setItem(CHUNK_RELOAD_KEY, currentHref);
          window.location.reload();
          return new Promise(() => undefined);
        }
      }

      throw error;
    }),
  );

  return (props: React.ComponentProps<T>) => (
    <Suspense fallback={<Skeleton active />}>
      <LazyComponent {...props} />
    </Suspense>
  );
}

const createLazyPages = (type: 'legacy' | 'new') => ({
  Organization: lazyLoad(() => import(`@pages/LoraWan/${type}/Organizations`)),
  Devices: lazyLoad(() => import(`@pages/LoraWan/${type}/Devices`)),
  DeviceDetails: lazyLoad(
    () => import(`@pages/LoraWan/${type}/Devices/DeviceDetails`),
  ),
  Dashboards: lazyLoad(() => import(`@pages/LoraWan/${type}/Dashboards`)),
  ActionsQueue: lazyLoad(() => import(`@pages/LoraWan/${type}/ActionsQueue`)),
  Forwardings: lazyLoad(() => import(`@pages/LoraWan/${type}/Forwardings`)),
  Transfers: lazyLoad(() => import(`@pages/LoraWan/${type}/Transfers`)),
  Contracts: lazyLoad(() => import(`@pages/LoraWan/${type}/Contracts`)),
  ContractDetails: lazyLoad(
    () => import(`@pages/LoraWan/${type}/Contracts/ContractDetails`),
  ),
  Requests: lazyLoad(() => import(`@pages/LoraWan/${type}/Requests`)),
  RequestDetails: lazyLoad(
    () => import(`@pages/LoraWan/${type}/Requests/RequestDetails`),
  ),
  Groupings: lazyLoad(() => import(`@pages/LoraWan/${type}/Groupings`)),
  DeviceProfiles: lazyLoad(
    () => import(`@pages/LoraWan/${type}/DeviceProfiles`),
  ),
  Connections: lazyLoad(() => import(`@pages/LoraWan/${type}/Connections`)),
});

const LAZY_PAGES = {
  legacy: createLazyPages('legacy'),
  new: createLazyPages('new'),
};

export type LoraPageKey = keyof typeof LAZY_PAGES.legacy;

export const LEGACY_LORAWAN_PAGES: LoraPageKey[] = [
  'Organization',
  'Devices',
  'DeviceDetails',
  'Dashboards',
  'ActionsQueue',
  'Forwardings',
  'Transfers',
  'Contracts',
  'ContractDetails',
  'Requests',
  'RequestDetails',
  'Groupings',
];

export const NEW_LORAWAN_PAGES: LoraPageKey[] = [
  'Devices',
  'DeviceDetails',
  'Dashboards',
  'ActionsQueue',
  'Transfers',
  'Contracts',
  'ContractDetails',
  'Requests',
  'RequestDetails',
  'Groupings',
  'DeviceProfiles',
  'Connections',
];

export const LORAWAN_PAGES_BY_TYPE: Record<'legacy' | 'new', LoraPageKey[]> = {
  legacy: LEGACY_LORAWAN_PAGES,
  new: NEW_LORAWAN_PAGES,
};

const createPageSwitcher = (pageKey: LoraPageKey) =>
  function LoraPageSwitcher(props: Record<string, unknown>) {
    const { hasPermission } = useContext(SessionContext);
    const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);

    const loraType: 'legacy' | 'new' = hasNewLora ? 'new' : 'legacy';

    if (!LORAWAN_PAGES_BY_TYPE[loraType].includes(pageKey)) return null;

    const PageComponent = LAZY_PAGES[loraType][pageKey];

    return <PageComponent {...props} />;
  };

const PAGES = {
  Organization: createPageSwitcher('Organization'),
  Devices: createPageSwitcher('Devices'),
  DeviceDetails: createPageSwitcher('DeviceDetails'),
  Dashboards: createPageSwitcher('Dashboards'),
  ActionsQueue: createPageSwitcher('ActionsQueue'),
  Forwardings: createPageSwitcher('Forwardings'),
  Transfers: createPageSwitcher('Transfers'),
  Contracts: createPageSwitcher('Contracts'),
  ContractDetails: createPageSwitcher('ContractDetails'),
  Requests: createPageSwitcher('Requests'),
  RequestDetails: createPageSwitcher('RequestDetails'),
  Groupings: createPageSwitcher('Groupings'),
  DeviceProfiles: createPageSwitcher('DeviceProfiles'),
  Connections: createPageSwitcher('Connections'),
};

export default function LoraPages() {
  return PAGES;
}
